import { dateFormat, isNotNull, useCurrentUserQuery, WorkLocationFragment } from '@alice-financial/alice-sdk'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ExternalLink,
  FullDialog,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@alice-financial/pretext-ui'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import ShareIcon from '@mui/icons-material/Share'
import { useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { PopupModal } from 'react-calendly'
import { useCurrentOrgRootPath, useOrg } from '../../organization/useOrg'

export const SchedulePreshiftStepTitle = 'Schedule Preshift'

export const SchedulePreshiftDescription = () => (
  <Typography variant="body2" color="text.secondary">
    Schedule your preshift meeting with Alice
  </Typography>
)

const PRESHIFT_CALENDLY_URL = import.meta.env.PROD
  ? 'https://calendly.com/grace-at-alice/pre-shift-enrollment-session'
  : 'https://calendly.com/hialice/preshift-test'
// Main component for the schedule preshift step
export const SchedulePreshiftComponent = () => {
  const queryClient = useQueryClient()
  const { data: currentUserData } = useCurrentUserQuery()
  const currentUser = currentUserData?.currentUser
  const org = useOrg()
  const [isCalendlyOpen, setIsCalendlyOpen] = React.useState(false)
  const [selectedLocation, setSelectedLocation] = React.useState<WorkLocationFragment | null>(null)
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false)
  const [shareLocation, setShareLocation] = React.useState<WorkLocationFragment | null>(null)
  const [isCopied, setIsCopied] = React.useState(false)

  if (!org) return null

  const locations = org.workLocations.filter(isNotNull)

  const handleScheduleClick = (location: WorkLocationFragment) => {
    setSelectedLocation(location)
    setIsCalendlyOpen(true)
  }

  const handleCalendlyClose = () => {
    setIsCalendlyOpen(false)
    setSelectedLocation(null)
    queryClient.invalidateQueries(useOrg.getKey())
  }

  const handleShareClick = (location: WorkLocationFragment) => {
    setShareLocation(location)
    setIsShareDialogOpen(true)
    setIsCopied(false)
  }

  const handleShareDialogClose = () => {
    setIsShareDialogOpen(false)
    setShareLocation(null)
    setIsCopied(false)
  }

  const buildShareableUrl = (location: WorkLocationFragment) => {
    const url = new URL(PRESHIFT_CALENDLY_URL)
    url.searchParams.set('utm_content', location.name)
    return url.toString()
  }

  const handleCopyToClipboard = async () => {
    if (!shareLocation) return

    const shareableUrl = buildShareableUrl(shareLocation)
    try {
      await navigator.clipboard.writeText(shareableUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const introText =
    locations.length > 1 ? (
      <>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          For each of your locations in the NYC area, please book a 10 minute time slot for one of our
          enrollment team members to introduce Alice to your employees. We&apos;ll personally help them
          enroll.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          For locations outside of the NYC area, please{' '}
          <ExternalLink href="mailto:admin-support@thisisalice.com">get in touch</ExternalLink> to arrange a
          time.
        </Typography>
      </>
    ) : (
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Schedule a time for our team to introduce Alice to your employees and answer any questions.
      </Typography>
    )

  return (
    <>
      <Box mb={3}>{introText}</Box>

      <List>
        {locations.map((location) => (
          <ListItem key={location.id} sx={{ flexDirection: 'column', alignItems: 'stretch', p: 0, mb: 2 }}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    {location.name}
                  </Typography>
                  {location.address && (
                    <Typography variant="body2" color="text.secondary">
                      {[
                        location.address.line1,
                        location.address.line2,
                        location.address.cityName,
                        location.address.stateCode,
                        location.address.zipcode,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Preshift Events:
                  </Typography>
                  <List dense sx={{ bgcolor: 'grey.50' }}>
                    {location.preshiftEvents.map((event) => {
                      const eventDate = event.scheduledAt ? new Date(event.scheduledAt) : null
                      const isPastEvent = eventDate ? eventDate < new Date() : false

                      return (
                        <ListItem key={event.id} divider>
                          <ListItemIcon>
                            <CalendarTodayIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              event.scheduledAt
                                ? `${dateFormat.medium(eventDate!)} at ${new Intl.DateTimeFormat('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                  }).format(eventDate!)}`
                                : 'Date TBD'
                            }
                            secondary={event.inviteeName && <>Booked by: {event.inviteeName}</>}
                          />
                          {isPastEvent && <Chip label="Past" color="default" size="small" disabled />}
                        </ListItem>
                      )
                    })}

                    {/* Schedule new event list item */}
                    <ListItem divider>
                      <ListItemIcon>
                        <CalendarTodayIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          location.preshiftEvents.length > 0
                            ? 'Schedule another preshift meeting'
                            : 'Schedule a preshift meeting'
                        }
                        secondary="Book 10 minutes on-site"
                      />
                      <Grid container spacing={1} wrap="nowrap">
                        <Grid size="auto">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ShareIcon />}
                            onClick={() => handleShareClick(location)}
                          >
                            Send link
                          </Button>
                        </Grid>
                        <Grid size="auto">
                          <Button
                            variant={location.preshiftEvents.length > 0 ? 'outlined' : 'contained'}
                            size="small"
                            startIcon={<CalendarTodayIcon />}
                            onClick={() => handleScheduleClick(location)}
                          >
                            Schedule
                          </Button>
                        </Grid>
                      </Grid>
                    </ListItem>
                  </List>
                </Box>
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>

      {/* Calendly Popup Dialog */}
      {selectedLocation && (
        <PopupModal
          url={PRESHIFT_CALENDLY_URL}
          pageSettings={{
            hideGdprBanner: true,
            hideEventTypeDetails: true,
          }}
          rootElement={document.getElementById('app') || document.body}
          prefill={{
            name: [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' '),
            email: currentUser?.email || undefined,
            firstName: currentUser?.firstName || undefined,
            lastName: currentUser?.lastName || undefined,
          }}
          utm={{
            utmSource: 'alice-web-app',
            utmMedium: 'work-locations',
            utmCampaign: 'nyc-scheduling',
            utmContent: selectedLocation.name,
          }}
          open={isCalendlyOpen}
          onModalClose={handleCalendlyClose}
        />
      )}

      {/* Share Dialog */}
      {shareLocation && (
        <FullDialog
          open={isShareDialogOpen}
          onClose={handleShareDialogClose}
          title={<Typography variant="h2">Share preshift booking link</Typography>}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share this link with others to let them book a preshift meeting for {shareLocation.name}:
            </Typography>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid size={12}>
                  <ExternalLink
                    href={buildShareableUrl(shareLocation)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}
                  >
                    {buildShareableUrl(shareLocation)}
                  </ExternalLink>
                </Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
                      <IconButton onClick={handleCopyToClipboard} color="primary" size="small" sx={{ ml: 1 }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </FullDialog>
      )}
    </>
  )
}

export const SchedulePreshiftInstructions = () => {
  const orgRootPath = useCurrentOrgRootPath()

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Schedule Preshift Meeting
      </Typography>

      <List dense>
        <ListItem disableGutters>
          <ListItemIcon>
            <LightbulbOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="The preshift meeting typically takes 10 minutes" />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon>
            <LightbulbOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="This meeting works best on or after your launch date" />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon>
            <LightbulbOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="Click 'Send link' to share the booking form with others who need to schedule" />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon>
            <LightbulbOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="Send the scheduling link to your GMs to book for their own locations" />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon>
            <LightbulbOutlinedIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText
            primary={
              <>
                Add General Managers for your locations on the{' '}
                <Link to={`${orgRootPath}/company-profile`}>Company Roles page</Link>. Send the scheduling
                link to them to book for their own locations
              </>
            }
          />
        </ListItem>
      </List>
    </Box>
  )
}
